import { useOutsideAlerter } from '@hooks';
import clsx from 'clsx';
import Link from 'next/link';
import { ButtonHTMLAttributes, useRef, useState } from 'react';

import { CircleLoader } from '../CircleLoader/CircleLoader';
import { Icon } from '../Icon/Icon';

import styles from './Button.module.scss';

interface LinkProps {
  open_in_new_tab?: boolean;
  url?: string;
  url_label: string;
  withoutBase?: boolean;
}

export interface SubItem {
  id?: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
  className?: string;
  view?: 'primary' | 'secondary' | 'tertiary' | 'animate';
  link?: LinkProps;
  download?: string;
  loading?: boolean;
  subitems?: SubItem[];
  size?: 'small' | 'medium' | 'large';
}

export const Button: React.FC<ButtonProps> = ({
  className,
  children,
  view = 'primary',
  link,
  id,
  download,
  loading,
  subitems,
  size = 'medium',
  onClick,
  ...props
}) => {
  const [isSubitemsOpen, setIsSubitemsOpen] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);

  useOutsideAlerter(buttonRef, () => setIsSubitemsOpen(false));

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (subitems && subitems.length > 0) {
      e.preventDefault();
      setIsSubitemsOpen(!isSubitemsOpen);
    }
    onClick?.(e);
  };

  const handleSubitemClick = (subitem: SubItem) => {
    if (!subitem.disabled) {
      subitem.onClick();
      setIsSubitemsOpen(false);
    }
  };

  if (download) {
    return (
      <a
        className={clsx(styles.button, styles[view], className)}
        href={link.url}
        download={download}
      >
        {link?.url_label}
      </a>
    );
  }

  if (link)
    return (
      <Link
        className={clsx(styles.button, styles[view], className)}
        href={(link?.withoutBase ? `${window.location.origin}${link?.url}` : link?.url) || '#'}
        target={link?.open_in_new_tab ? '_blank' : '_self'}
        rel={link?.open_in_new_tab ? 'noopener noreferrer' : ''}
        id={id}
        download={download}
      >
        {loading ? '' : link?.url_label}
      </Link>
    );

  const buttonElement = (
    <button
      id={id}
      {...props}
      onClick={handleButtonClick}
      className={clsx(styles.button, styles[view], styles[size], className, {
        [styles.loading]: loading,
        [styles.withSubitems]: subitems && subitems.length > 0,
      })}
    >
      {loading ? (
        <CircleLoader
          small
          key={id}
        />
      ) : (
        children
      )}
      {subitems && subitems.length > 0 && (
        <span className={clsx(styles.subitemsIndicator, { [styles.open]: isSubitemsOpen })}>
          <Icon
            name="angle-right"
            style={{ transform: 'rotate(90deg)' }}
          />
        </span>
      )}
    </button>
  );

  if (subitems && subitems.length > 0) {
    return (
      <div
        ref={buttonRef}
        className={styles.buttonWrapper}
      >
        {buttonElement}
        {isSubitemsOpen && (
          <div className={styles.subitemsList}>
            {subitems.map(subitem => (
              <button
                key={subitem.id || subitem.label}
                type="button"
                className={clsx(styles.subitem, { [styles.disabled]: subitem.disabled })}
                onClick={() => handleSubitemClick(subitem)}
                disabled={subitem.disabled}
              >
                {subitem.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return buttonElement;
};
